import { useEffect, useRef } from "react";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";
import { Box, makeStyles, TextField } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  input: {
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    "& .MuiInputBase-root": {
      backgroundColor: theme.palette.common.white,
      borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
      overflow: "hidden",
      transition: "box-shadow 160ms ease, border-color 160ms ease",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.border.input,
      borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.light,
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: `0 0 0 3px ${theme.palette.surface.accentSoft}`,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-input": {
      fontSize: 13,
      padding: theme.spacing(1.6, 1.75),
    },
  },
  suggestionsContainer: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    overflow: "hidden",
    background: `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${theme.palette.surface.soft} 100%)`,
    boxShadow: theme.palette.shadow.card,
  },
  suggestionsList: {
    padding: 0,
    width: "100%",
    maxHeight: "180px",
    overflow: "auto",
    margin: 0,
    cursor: "pointer",
    "&::-webkit-scrollbar": {
      ...theme.scrollbars.webkitScrollbar,
    },
    "&::-webkit-scrollbar-track": {
      ...theme.scrollbars.webkitScrollbarTrack,
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.status.scrollbarThumb,
      borderRadius: 999,
    },
    "& > li": {
      margin: 0,
      background: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.border.soft}`,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      padding: theme.spacing(1, 1.25),
      color: theme.palette.text.primary,
      transition: "background-color 140ms ease, color 140ms ease",
    },
    "& > li:hover": {
      background: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },
  suggestionItem: {
    listStyleType: "none",
  },
}));

const PlacesAutocomplete = ({
  extraField,
  setExtraField,
  label = "",
  placeholder = "Direccion",
  textFieldProps = {},
}) => {
  const classes = useStyles();
  const previousAddressRef = useRef(extraField?.address || "");
  const {
    className,
    disabled: disabledProp,
    error: errorProp,
    helperText: helperTextProp,
    ...restTextFieldProps
  } = textFieldProps;

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
    },
    debounce: 300,
    defaultValue: extraField.address,
  });

  useEffect(() => {
    const nextAddress = extraField?.address || "";
    if (nextAddress !== previousAddressRef.current) {
      previousAddressRef.current = nextAddress;
      setValue(nextAddress, false);
    }
  }, [extraField?.address, setValue]);

  const ref = useOnclickOutside(() => {
    // When user clicks outside of the component, we can dismiss
    // the searched suggestions by calling this method
    clearSuggestions();
  });

  const handleInput = (e) => {
    const nextAddress = e.target.value;

    // Update the keyword of the input element
    setValue(nextAddress);

    // Hasta que el usuario seleccione una opcion valida de Google,
    // mantenemos el campo en estado no validado.
    setExtraField({
      ...extraField,
      address: nextAddress,
      coordinates: nextAddress ? "0,0" : "",
      ciudad: "",
      provincia: "",
      pais: "",
    });
  };

  // Funcion para extraer provincia y pais de los componentes de la direccion
  const extractAddressComponents = (addressComponents) => {
    let provincia = "";
    let pais = "";
    let ciudad = "";

    for (const component of addressComponents) {
      const types = component.types;

      // Buscar ciudad
      if (types.includes("locality")) {
        ciudad = component.long_name;
      }
      // Buscar provincia/estado
      else if (types.includes("administrative_area_level_1")) {
        provincia = component.long_name;
      }
      // Buscar pais
      else if (types.includes("country")) {
        pais = component.long_name;
      }
    }

    return { provincia, pais, ciudad };
  };

  const handleSelect =
    ({ description }) =>
    () => {
      // When user selects a place, we can replace the keyword without request data from API
      // by setting the second parameter to "false"
      setValue(description, false);
      clearSuggestions();

      // Get latitude and longitude via utility functions
      getGeocode({ address: description }).then((results) => {
        const { lat, lng } = getLatLng(results[0]);

        const addressComponents = results[0].address_components;
        const { provincia, pais, ciudad } = extractAddressComponents(addressComponents);

        setExtraField({
          ...extraField,
          address: description,
          coordinates: `${lat},${lng}`,
          ciudad,
          provincia,
          pais,
        });
      });
    };

  const renderSuggestions = () =>
    data.map((suggestion) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      return (
        <li key={place_id} onClick={handleSelect(suggestion)} className={classes.suggestionItem}>
          <strong>{main_text}</strong> <small>{secondary_text}</small>
        </li>
      );
    });

  return (
    <div ref={ref}>
      <TextField
        error={errorProp ?? extraField.coordinates === "0,0"}
        fullWidth
        value={value}
        onChange={handleInput}
        disabled={disabledProp ?? !ready}
        placeholder={placeholder}
        label={label}
        variant="outlined"
        className={[classes.input, className].filter(Boolean).join(" ")}
        helperText={
          helperTextProp ??
          (extraField.coordinates === "0,0" && "Seleccione una direccion valida de Google")
        }
        {...restTextFieldProps}
      />
      {status === "OK" && (
        <Box className={classes.suggestionsContainer}>
          <ul className={classes.suggestionsList}>{renderSuggestions()}</ul>
        </Box>
      )}
    </div>
  );
};

export default PlacesAutocomplete;
